import { gql } from "@apollo/client";

import {
  CatalogEntryDetails,
  catalogEntryDetails,
} from "shared/catalog/AvailableCatalog/available-catalog.helper";
import {
  ProductSectionDocument,
  ProductSectionQuery,
} from "shared/product/ProductSection/product-section.query.generated";
import { StrapiImageData, stripMetadata } from "ui/StrapiImage";
import { features } from "utils/feature-toggle/Decision";
import { applyTax } from "utils/price.utils";
import { initializeApollo } from "utils/with-apollo.utils";

import {
  AvailableReplacementCatalogueDocument,
  AvailableReplacementCatalogueQuery,
  AvailableReplacementCatalogueQueryVariables,
} from "./product-section.helper.generated";

const SPLIT_AT_FIRST_DASH = /-([\s\S]*)/;

export const extractStorageAndColorSlug = (referenceSlug?: string) =>
  referenceSlug?.split(SPLIT_AT_FIRST_DASH) ?? [];

export type ProductSectionData = Awaited<
  ReturnType<typeof getProductSectionData>
>;

type ProductAdvantage = {
  icon: string;
  title: string;
  content: string;
};

type ProductDetailItem = {
  title: string;
  text: string;
};

type ProductDetail = {
  title: string;
  items: ProductDetailItem[];
};

type ProductDescription = {
  subtitle: string;
  content: string;
  details: ProductDetail[];
  photo?: StrapiImageData;
};

type ProductTechnicalItem = {
  name: string;
  value: string;
};

type ProductTechnicalSection = {
  title: string;
  items: ProductTechnicalItem[];
};

type ProductTechnical = {
  sections: ProductTechnicalSection[];
};

type ProductFeatureItem = {
  title: string;
  text: string;
};

type ProductFeature = {
  title: string;
  items: ProductFeatureItem[];
};

type ProductReviewItem = {
  icon: string;
  title: string;
  text: string;
};

type ProductReview = {
  rating: string;
  source: string;
  items: ProductReviewItem[];
};

type ProductContentDetail = {
  advantages: ProductAdvantage[];
  description: ProductDescription;
  technical: ProductTechnical;
  features: ProductFeature[];
  review?: ProductReview;
};

gql`
  # {"productSlug": "iphone-12-pro"}
  query availableReplacementCatalogue($productSlug: String!, $model: String!) {
    replacementCatalogue: b2CSmartphoneCatalog {
      data {
        attributes {
          catalogId
          entries(
            filters: {
              category: { eq: "available" }
              subscriptions: {
                smartphoneVariant: {
                  smartphoneFamily: { slug: { eq: $productSlug } }
                }
              }
            }
          ) {
            id
            entryName
            isNew
            formerUpfrontPriceInCentsInclundingTax
            blackFridayFormerUpfrontPriceInCentsInclundingTax
            flagship {
              data {
                id
                attributes {
                  smartphoneVariant {
                    data {
                      id
                      attributes {
                        slug
                        storage
                        colorName
                        combinedPicture {
                          data {
                            id
                            attributes {
                              alternativeText
                              formats
                            }
                          }
                        }
                        smartphoneFamily {
                          data {
                            id
                            attributes {
                              slug
                              brand
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            subscriptionDrop {
              formerSubscriptionPriceInCentsIncludingTax
            }
            subscriptions {
              data {
                id
                attributes {
                  subscriptionItem {
                    id
                    properties
                    priceExcludingTax: sell_price_in_cents_excl_tax
                    tax {
                      id
                      amount
                    }
                    stock {
                      isOnline: is_online
                    }
                  }
                  smartphoneVariant {
                    data {
                      id
                      attributes {
                        slug
                        storage
                        colorName
                        colorCode
                      }
                    }
                  }
                }
              }
            }
            tags {
              data {
                id
                attributes {
                  name
                  slug
                }
              }
            }
          }
        }
      }
    }

    # {"model": "iphone12pro"}
    upfronts: items(
      where: {
        item_type: { _eq: "Upfront" }
        _not: { properties: { _contains: { private: true } } }
        properties: { _contains: { model: $model } }
      }
    ) {
      id
      properties
      priceExcludingTax: sell_price_in_cents_excl_tax
      tax {
        id
        amount
      }
    }
  }
`;

export type SmartphoneFamilyDescription = {
  advantages: ProductAdvantage[];
  description: Omit<ProductDescription, "photo">;
  technical: ProductTechnical;
  technical_universal?: ProductTechnical;
  features: ProductFeature[];
  review?: ProductReview;
};

export type Variant = {
  sku: string | null;
  id: number | undefined;
  subscriptionPrice: number;
  subscriptionModelName: any;
  upfrontPrice: number;
  originalUpfrontPrice: number | null;
  originalSubscriptionPrice: number | null;
  variantSlug: string | null;
  storage: string | null;
  storageSlug: string;
  color: string | null;
  colorSlug: string;
  backPicture: StrapiImageData | null;
  frontPicture: StrapiImageData | null;
  combinedPicture: StrapiImageData | null;
  isInStock: boolean;
  tagSlugs: (string | undefined)[] | undefined;
  mustBePurchased: boolean;
  commitmentPeriod?: number;
  hasUpfrontPriceDrop?: boolean;
  hasSubscriptionPriceDrop?: boolean;
};

export const getProductSectionData = async (productSlug: string) => {
  const apolloClient = initializeApollo();

  const { data: productPageData } =
    await apolloClient.query<ProductSectionQuery>({
      query: ProductSectionDocument,
      variables: { productSlug },
    });

  const [entry] =
    productPageData?.productEntries?.data?.attributes?.entries || [];
  const isBlackFriday =
    productPageData?.websiteSetting?.data?.attributes?.isBlackFriday ?? false;
  const category = entry?.category ?? null;
  const releaseDate = entry?.releaseDate ?? null;
  const modelName =
    productPageData?.smartphoneFamilies?.data?.map(
      ({ attributes }) => attributes?.model,
    )[0] ?? null;

  const ogImageUrl =
    productPageData?.smartphoneFamilies?.data?.map(({ attributes }) =>
      stripMetadata(attributes?.ogImage?.data?.attributes),
    )[0]?.large?.url ?? "";

  const brand =
    productPageData?.smartphoneFamilies?.data?.map(
      ({ attributes }) => attributes?.brand,
    )[0] ?? null;
  const dasValues =
    productPageData?.smartphoneFamilies?.data
      ?.map(({ attributes }) => attributes?.dasValues)[0]
      ?.split("\n")
      .map((dasValue) => dasValue.trim()) ?? [];

  const variants: Variant[] | undefined = entry?.subscriptions?.data?.map(
    ({ attributes }) => {
      const subscriptionItem = attributes?.subscriptionItem;
      const variantEntry = attributes?.smartphoneVariant?.data?.attributes;

      const sku = attributes?.sku ?? null;
      const storage = variantEntry?.storage ?? null;
      const color = variantEntry?.colorName ?? null;
      const subscriptionModelName = subscriptionItem?.properties?.model;

      const combinedPicture =
        stripMetadata(variantEntry?.combinedPicture?.data?.attributes) ?? null;
      const frontPicture =
        stripMetadata(variantEntry?.frontPicture?.data?.attributes) ?? null;
      const backPicture =
        stripMetadata(variantEntry?.backPicture?.data?.attributes) ?? null;

      const variantSlug = variantEntry?.slug ?? null;
      const [storageSlug, colorSlug] = extractStorageAndColorSlug(
        variantEntry?.slug,
      ) ?? [null, null];

      const subscriptionPrice = applyTax(
        subscriptionItem?.priceExcludingTax ?? 0,
        subscriptionItem?.tax?.amount,
      );

      const upfrontPrice =
        productPageData?.upfronts
          ?.filter(
            ({ properties }) =>
              properties?.model === subscriptionItem?.properties?.model &&
              properties?.storage === subscriptionItem?.properties?.storage &&
              properties?.contractTerm ===
                subscriptionItem?.properties?.contractTerm &&
              properties?.mustBePurchased ===
                subscriptionItem?.properties?.mustBePurchased,
          )
          ?.map(({ priceExcludingTax, tax }) =>
            applyTax(priceExcludingTax ?? 0, tax?.amount),
          )[0] ?? null;

      const isRestrictedModel = features.isRestrictedModel(
        subscriptionModelName,
      );

      // For iPhone 17/Air models, disable brand new ordering by forcing mustBePurchased to false
      const mustBePurchased = isRestrictedModel
        ? false
        : Boolean(subscriptionItem?.properties?.mustBePurchased);
      const isOnline = Boolean(subscriptionItem?.stock?.isOnline);
      const shouldIgnoreOutOfStock = features.ignoreOutOfStock(
        subscriptionModelName,
      );
      // For iPhone 17/Air models, ignore isMustBePurchased and only check isOnline
      const isInStock = shouldIgnoreOutOfStock
        ? true
        : isRestrictedModel
        ? isOnline
        : mustBePurchased || isOnline;

      const originalUpfrontDropPrice =
        entry?.subscriptionDrop?.find(
          (subscriptionDropEntry) =>
            subscriptionDropEntry?.subscriptions?.data.some(
              (subscription) => subscription?.attributes?.sku === sku,
            ),
        )?.formerUpfrontPriceInCentsIncludingTax || null;

      const originalUpfrontPrice =
        originalUpfrontDropPrice ??
        (isBlackFriday
          ? entry?.blackFridayFormerUpfrontPriceInCentsInclundingTax
          : entry?.formerUpfrontPriceInCentsInclundingTax) ??
        null;

      const hasUpfrontPriceDrop =
        !!originalUpfrontPrice && upfrontPrice < originalUpfrontPrice;

      const originalSubscriptionPrice =
        entry?.subscriptionDrop?.find(
          (subscriptionDropEntry) =>
            subscriptionDropEntry?.subscriptions?.data.some(
              (subscription) => subscription?.attributes?.sku === sku,
            ),
        )?.formerSubscriptionPriceInCentsIncludingTax || null;

      const hasSubscriptionPriceDrop =
        !!originalSubscriptionPrice &&
        subscriptionPrice < originalSubscriptionPrice;

      const tagSlugs = entry?.tags?.data?.map(
        ({ attributes: attribute }) => attribute?.slug,
      );

      const commitmentPeriod =
        subscriptionItem?.properties?.contractTerm ?? null;

      if (!subscriptionItem) {
        const debugInfo = {
          productSlug,
          sku,
          variantSlug,
          storage,
          color,
          subscriptionModelName,
          entryId: entry?.id ?? null,
        };
        throw new Error(
          `Subscription item is missing for variant. Debug: ${JSON.stringify(
            debugInfo,
          )}`,
        );
      }
      return {
        sku,
        id: subscriptionItem.id,
        subscriptionPrice,
        subscriptionModelName,
        upfrontPrice,
        originalUpfrontPrice,
        originalSubscriptionPrice,
        variantSlug,
        storage,
        storageSlug,
        color,
        colorSlug,
        backPicture,
        frontPicture,
        combinedPicture,
        isInStock,
        tagSlugs,
        commitmentPeriod,
        mustBePurchased,
        hasUpfrontPriceDrop,
        hasSubscriptionPriceDrop,
      };
    },
  );

  const replacement =
    (await productPageData?.smartphoneFamilies?.data?.map(
      async ({ attributes }) => {
        const replacementFamily = attributes?.replacement?.data?.attributes;

        const replacementSubscriptionModel =
          replacementFamily?.smartphoneVariants?.data?.[0].attributes
            ?.smartphoneSubscriptions?.data?.[0].attributes?.subscriptionItem
            ?.properties.model;

        const replacementVariant =
          replacementFamily?.smartphoneVariants?.data?.map(
            ({ attributes: variantAttributes }) => ({
              slug: variantAttributes?.slug,
              color: variantAttributes?.colorName,
              storage: variantAttributes?.storage,
              combinedPicture:
                stripMetadata(
                  variantAttributes?.combinedPicture?.data?.attributes,
                ) ?? null,
            }),
          )?.[0];

        if (!replacementFamily || !replacementVariant) {
          return null;
        }

        const { data: replacementData } = await apolloClient.query<
          AvailableReplacementCatalogueQuery,
          AvailableReplacementCatalogueQueryVariables
        >({
          query: AvailableReplacementCatalogueDocument,
          variables: {
            productSlug: replacementFamily.slug,
            model: replacementSubscriptionModel,
          },
        });

        const availableReplacementUpfronts = replacementData?.upfronts;
        const replacementEntry =
          replacementData?.replacementCatalogue?.data?.attributes?.entries?.[0];

        const replacementCatalogueEntryDetail: CatalogEntryDetails =
          catalogEntryDetails({
            catalogueEntry: replacementEntry!,
            entryUpfronts: availableReplacementUpfronts,
            isBlackFriday,
          });

        return {
          modelName: replacementFamily.model ?? null,
          modelSlug: `/${replacementFamily.slug}`,
          picture: replacementVariant?.combinedPicture,
          color: replacementVariant?.color,
          storage: replacementVariant?.storage,
          availableCatalogueEntry: replacementCatalogueEntryDetail,
        };
      },
    )[0]) ?? null;

  const tips =
    productPageData?.smartphoneFamilies?.data?.map(
      ({ attributes }) => attributes?.tips,
    )[0] ?? null;

  const family = {
    category,
    modelName,
    brand,
    releaseDate,
    dasValues,
    replacement,
    ogImageUrl,
    tips,
  };

  // array with all skus of all items in the subscriptionDrop
  const subscriptionDropSkus =
    entry?.subscriptionDrop?.flatMap(
      (subscriptionDropEntry) =>
        subscriptionDropEntry?.subscriptions?.data.map(
          (item) => item?.attributes?.sku,
        ),
    ) || [];

  const descriptionPicture =
    productPageData?.smartphoneFamilies?.data?.map(
      ({ attributes }) =>
        stripMetadata(attributes?.descriptionImage?.data?.attributes) ?? null,
    )[0] ?? null;

  const smartphoneFamilyDescription =
    (productPageData?.smartphoneFamilies?.data?.map(
      ({ attributes }) => attributes?.description,
    )[0] as SmartphoneFamilyDescription) ?? null;

  const productDetail: ProductContentDetail | null = smartphoneFamilyDescription
    ? {
        ...smartphoneFamilyDescription,
        ...(descriptionPicture && {
          description: {
            ...smartphoneFamilyDescription.description,
            photo: descriptionPicture,
          },
        }),
      }
    : null;

  const hasVariantWithUpfrontPriceDrop =
    variants?.some((variant) => variant.hasUpfrontPriceDrop) ?? false;

  const hasVariantWithSubscriptionPriceDrop =
    variants?.some((variant) => variant.hasSubscriptionPriceDrop) ?? false;

  const isPreOrder = entry?.isPreOrder || false;

  const deliveryDate = entry?.deliveryDate;

  return {
    family,
    variants,
    subscriptionDropSkus,
    productDetail,
    hasVariantWithUpfrontPriceDrop,
    hasVariantWithSubscriptionPriceDrop,
    isPreOrder,
    deliveryDate,
  };
};
